import React, { useState, useEffect } from 'react';
import { usePreview } from '@/contexts/PreviewContext';
import { organizationService, Organization } from '@/firebase/organizationService';
import { useNotification } from '@/contexts/NotificationContext';
import { getInvitationsByOrganization } from '@/services/invitationService';
import { getUserSystemRoleForOrganization } from '@/utils/roleHelpers';
import { Eye, Loader2, Search, User } from 'lucide-react';
import { SYSTEM_ROLES } from '@/constants/roles';

type UserOption = {
  id: string;
  name: string;
  email: string;
  roles?: string[];
  role?: string;
  organizationRoles?: Record<string, { systemRole: string; ownerRoles?: string[] }>;
  locationIds?: string[];
  isInactive?: boolean; // Invite sent but not yet accepted
};

const AdminPreviewPanel: React.FC = () => {
  const { previewState, isPreviewMode, enterPreviewMode, exitPreviewMode, canUsePreview } = usePreview();
  const { showNotification } = useNotification();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<string>('user');
  const [previewMode, setPreviewMode] = useState<'role' | 'user'>('role');
  const [users, setUsers] = useState<UserOption[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (canUsePreview) {
      loadOrganizations();
    }
  }, [canUsePreview]);

  // Load users when organization is selected and preview mode is "user"
  useEffect(() => {
    if (selectedOrgId && previewMode === 'user') {
      loadUsersForOrganization(selectedOrgId);
    } else {
      setUsers([]);
      setSelectedUserId('');
    }
  }, [selectedOrgId, previewMode]);

  const loadOrganizations = async () => {
    setIsLoading(true);
    try {
      const result = await organizationService.getAllOrganizations();
      if (result.success && result.data) {
        setOrganizations(result.data);
      } else {
        showNotification('Failed to load organizations', 'error');
      }
    } catch (error: any) {
      console.error('Error loading organizations:', error);
      showNotification(`Error: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadUsersForOrganization = async (organizationId: string) => {
    setIsLoadingUsers(true);
    try {
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      const { db } = await import('@/firebase/config');
      const usersRef = collection(db, 'users');
      
      // Query 1: Users with organizationId matching current organization (legacy support)
      const orgIdQuery = query(usersRef, where('organizationId', '==', organizationId));
      
      // Query 2: Users with organizationIds array containing current organization
      const orgIdsQuery = query(usersRef, where('organizationIds', 'array-contains', organizationId));
      
      // Query 3: Pending invitations for this organization (invite sent but not yet accepted)
      const invitationsResult = getInvitationsByOrganization(organizationId);
      
      // Execute all queries in parallel
      const [orgIdSnapshot, orgIdsSnapshot, invitationsResp] = await Promise.all([
        getDocs(orgIdQuery),
        getDocs(orgIdsQuery),
        invitationsResult
      ]);
      
      // Combine results and deduplicate by user ID (active users)
      const usersMap = new Map<string, UserOption>();
      
      // Process users from organizationId query
      orgIdSnapshot.forEach((userDoc) => {
        const userData = userDoc.data();
        usersMap.set(userDoc.id, {
          id: userDoc.id,
          name: userData.name || userData.displayName || userData.email || 'Unknown',
          email: userData.email || '',
          roles: userData.roles || (userData.role ? (Array.isArray(userData.role) ? userData.role : [userData.role]) : []),
          role: userData.role,
          organizationRoles: userData.organizationRoles,
          locationIds: userData.locationIds || [],
          isInactive: false
        });
      });
      
      // Process users from organizationIds array query (skip duplicates)
      orgIdsSnapshot.forEach((userDoc) => {
        if (usersMap.has(userDoc.id)) return; // Skip if already added
        const userData = userDoc.data();
        usersMap.set(userDoc.id, {
          id: userDoc.id,
          name: userData.name || userData.displayName || userData.email || 'Unknown',
          email: userData.email || '',
          roles: userData.roles || (userData.role ? (Array.isArray(userData.role) ? userData.role : [userData.role]) : []),
          role: userData.role,
          organizationRoles: userData.organizationRoles,
          locationIds: userData.locationIds || [],
          isInactive: false
        });
      });
      
      // Add pending invitations as inactive (exclude if user already exists - e.g. accepted via different flow)
      const activeEmails = new Set(Array.from(usersMap.values()).map(u => u.email?.toLowerCase()));
      if (invitationsResp.success && invitationsResp.invitations) {
        for (const inv of invitationsResp.invitations) {
          if (inv.email && !activeEmails.has(inv.email.toLowerCase())) {
            usersMap.set(`invitation-${inv.id}`, {
              id: `invitation-${inv.id}`,
              name: inv.name || inv.email || 'Unknown',
              email: inv.email,
              isInactive: true
            });
          }
        }
      }
      
      // Convert map to array and sort by name (active users first, then inactive)
      const usersList = Array.from(usersMap.values());
      usersList.sort((a, b) => {
        // Active users first
        if (a.isInactive !== b.isInactive) return a.isInactive ? 1 : -1;
        return a.name.localeCompare(b.name);
      });
      setUsers(usersList);
    } catch (error: any) {
      console.error('Error loading users:', error);
      showNotification(`Error loading users: ${error.message}`, 'error');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleEnterPreview = async () => {
    if (!selectedOrgId) {
      showNotification('Please select an organization', 'error');
      return;
    }

    const selectedOrg = organizations.find(org => org.id === selectedOrgId);
    if (!selectedOrg) {
      showNotification('Selected organization not found', 'error');
      return;
    }

    try {
      if (previewMode === 'user') {
        if (!selectedUserId) {
          showNotification('Please select a user', 'error');
          return;
        }

        const selectedUser = users.find(u => u.id === selectedUserId);
        if (!selectedUser) {
          showNotification('Selected user not found', 'error');
          return;
        }
        if (selectedUser.isInactive) {
          showNotification('Cannot preview as inactive user. User must accept the invitation first.', 'error');
          return;
        }

        // Determine the system role - use org-specific role when available
        const systemRole = getUserSystemRoleForOrganization(selectedUser as any, selectedOrgId)
          || (selectedUser.roles || (selectedUser.role ? [selectedUser.role] : [])).find((r: string) => SYSTEM_ROLES.includes(r as any))
          || 'user';
        
        await enterPreviewMode(
          selectedOrgId,
          selectedOrg.name,
          systemRole,
          selectedUser.locationIds || [],
          'user',
          selectedUser.id,
          selectedUser.name,
          selectedUser.email
        );
        showNotification(`Preview mode: ${selectedOrg.name} as ${selectedUser.name} (${selectedUser.email})`, 'success');
      } else {
        await enterPreviewMode(
          selectedOrgId,
          selectedOrg.name,
          selectedRole,
          [],
          'role'
        );
        showNotification(`Preview mode: ${selectedOrg.name} as ${selectedRole}`, 'success');
      }
    } catch (error: any) {
      console.error('Error entering preview mode:', error);
      showNotification(`Error: ${error.message}`, 'error');
    }
  };

  const handleExitPreview = async () => {
    try {
      await exitPreviewMode();
      showNotification('Exited preview mode', 'success');
      setSelectedOrgId('');
      setSelectedRole('user');
      setSelectedUserId('');
      setPreviewMode('role');
    } catch (error: any) {
      console.error('Error exiting preview mode:', error);
      showNotification(`Error: ${error.message}`, 'error');
    }
  };

  // Filter organizations by search query (only by name, not ID)
  const filteredOrganizations = organizations.filter(org =>
    org.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!canUsePreview) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-4">
        <Eye className="w-5 h-5 text-primary-600" />
        <h2 className="text-xl font-semibold text-gray-900">Customer Preview</h2>
      </div>

      {isPreviewMode && previewState ? (
        <div className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-yellow-900">
                  Currently previewing: {previewState.organizationName}
                </p>
                <p className="text-sm text-yellow-700 mt-1">
                  {previewState.previewType === 'user' && previewState.previewUserName
                    ? `User: ${previewState.previewUserName} (${previewState.previewUserEmail})`
                    : `Role: ${previewState.role}`}
                </p>
              </div>
              <button
                onClick={handleExitPreview}
                className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors font-medium"
              >
                Exit Preview
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search organizations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Organization Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Organization
            </label>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
              </div>
            ) : (
              <select
                value={selectedOrgId}
                onChange={(e) => setSelectedOrgId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">-- Select an organization --</option>
                {filteredOrganizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Preview Mode Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preview Mode
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setPreviewMode('role');
                  setSelectedUserId('');
                }}
                className={`flex-1 px-4 py-2 rounded-md border transition-colors ${
                  previewMode === 'role'
                    ? 'bg-primary-50 border-primary-500 text-primary-700 font-medium'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                By Role
              </button>
              <button
                type="button"
                onClick={() => {
                  setPreviewMode('user');
                  setSelectedRole('user');
                }}
                className={`flex-1 px-4 py-2 rounded-md border transition-colors ${
                  previewMode === 'user'
                    ? 'bg-primary-50 border-primary-500 text-primary-700 font-medium'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                By User
              </button>
            </div>
          </div>

          {/* Role Selection (only show if preview mode is "role") */}
          {previewMode === 'role' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preview As Role
              </label>
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {SYSTEM_ROLES.map((role) => (
                  <option key={role} value={role}>
                    {role.charAt(0).toUpperCase() + role.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* User Selection (only show if preview mode is "user") */}
          {previewMode === 'user' && selectedOrgId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select User
              </label>
              {isLoadingUsers ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
                </div>
              ) : (
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">-- Select a user --</option>
                  {users.map((user) => (
                    <option
                      key={user.id}
                      value={user.id}
                      disabled={user.isInactive}
                    >
                      {user.isInactive
                        ? `${user.name} (${user.email}) — Invite sent (inactive)`
                        : `${user.name} (${user.email})`}
                    </option>
                  ))}
                </select>
              )}
              {users.length === 0 && !isLoadingUsers && selectedOrgId && (
                <p className="text-sm text-gray-500 mt-1">No users found in this organization</p>
              )}
            </div>
          )}

          {/* Enter Preview Button */}
          <button
            onClick={handleEnterPreview}
            disabled={
              !selectedOrgId ||
              isLoading ||
              (previewMode === 'user' && !selectedUserId) ||
              (previewMode === 'user' && isLoadingUsers) ||
              (previewMode === 'user' && !!users.find(u => u.id === selectedUserId)?.isInactive)
            }
            className="w-full px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
          >
            <Eye className="w-4 h-4" />
            Enter Preview Mode
          </button>

          {/* Organization Count */}
          {!isLoading && (
            <p className="text-sm text-gray-500 text-center">
              {filteredOrganizations.length} organization{filteredOrganizations.length !== 1 ? 's' : ''} found
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminPreviewPanel;

